import { inject, Injectable } from '@angular/core';

import { take } from 'rxjs';

import {
  ImportSkillsStep,
  ImportSkillsStepDefinition,
  ImportSkillsStepI,
} from '@core/tellent-skills-types';
import { DialogRef, DialogService } from '@core/user-interface-legacy';

import { importSkillsStepComponentMap } from './import-skills-flow-component.map';
import { ImportSkillsFlowStateService } from './import-skills-flow-state.service';

@Injectable({ providedIn: 'root' })
export class ImportSkillsFlowFacade {
  private readonly dialogService = inject(DialogService);
  private readonly flowStateService = inject(ImportSkillsFlowStateService);
  private readonly stepComponentMap: Record<ImportSkillsStep, ImportSkillsStepI> =
    importSkillsStepComponentMap;

  private activeDialogRef?: DialogRef;

  public startImportFlow(): void {
    this.cancelCurrentFlow();
    const firstStep = this.flowStateService.initializeFlow();
    if (firstStep) {
      this.openStepDialog(firstStep);
    }
  }

  private openStepDialog(stepDef: ImportSkillsStepDefinition): void {
    const stepToOpen = this.stepComponentMap[stepDef.step];

    if (!stepToOpen) {
      console.error(`No component registered for step: ${stepDef.step}`);
      return;
    }

    this.flowStateService.flowData$.pipe(take(1)).subscribe(data => {
      this.activeDialogRef = this.dialogService.openDialog(
        stepToOpen.dialogComponent,
        { ...data, ...stepDef },
        stepToOpen.dialogConfig,
      );

      this.activeDialogRef.afterClosed.pipe(take(1)).subscribe(result => {
        if (result === undefined) {
          this.activeDialogRef = undefined;
          return;
        }

        const nextStep = this.flowStateService.advanceFlow(result);
        if (nextStep) {
          this.openStepDialog(nextStep);
        } else {
          this.activeDialogRef = undefined;
        }
      });
    });
  }

  private cancelCurrentFlow(): void {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
      this.activeDialogRef = undefined;
    }
  }
}
